import { Color, Spacing } from '@persona-id/design-tokens';
import Fuse from 'fuse.js';
import { Fragment, useMemo, useState } from 'react';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import SearchInput from 'dashboard/components-lib/SearchInput';
import { formatAddress } from 'dashboard/lib/utils/formatters';
import { nameCase } from 'dashboard/lib/utils/stringFormat';
import {
  FilingStatus,
  ReportBusinessLiensLookup,
  UCCFiling,
} from 'dashboard/models/report/business-liens-lookup';
import Attribute from 'design-system/components/Attribute';
import AttributeGrid from 'design-system/components/Attribute/AttributeGrid';
import Card from 'design-system/components/Card/Card';
import Sticker from 'design-system/components/Sticker/Sticker';
import { Heading, Paragraph } from 'design-system/components/Text';
import { US_STATES, USState } from 'lib/constants/locales';

const UnorderedList = styled('ul')`
  margin-left: ${Spacing[4]};
  line-height: ${({ theme }) => theme.text.paragraph.md.lineHeight};
`;

const StyledCard = styled(Card)`
  border-radius: 5px;
  border: 1px solid ${Color.CoreSlate200};
`;

const HorizontalContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing[3]};
`;

const StatusSticker = ({ status }: { status: FilingStatus | undefined }) => {
  switch (status) {
    case FilingStatus.Open:
      return <Sticker variant='danger'>Open</Sticker>;
    case FilingStatus.Closed:
      return <Sticker variant='green'>Closed</Sticker>;
    default:
      return <Sticker variant='grey'>Unknown</Sticker>;
  }
};

interface UCCFilingDetailsProps {
  filing: UCCFiling;
  isRedacted: boolean;
}

const UCCFilingDetails = ({ filing, isRedacted }: UCCFilingDetailsProps) => {
  const {
    'document-type': documentType,
    'expiration-date': expirationDate,
    'filing-date': filingDate,
    'filing-office': filingOffice,
    'filing-reference': filingReference,
    'original-filing-date': originalFilingDate,
    'original-filing-reference': originalFilingReference,
    'secured-parties': securedParties,
    collaterals,
    debtors,
    status,
  } = filing;

  const jusrisdiction =
    filingOffice?.['address-region'] &&
    US_STATES[filingOffice?.['address-region'].toLocaleUpperCase() as USState];

  const primarySecuredParty = securedParties?.[0];

  return (
    <StyledCard>
      <Card.Body>
        <Card.Title>
          <div>
            <HorizontalContainer>
              {nameCase(primarySecuredParty?.name)}
              <StatusSticker status={status} />
            </HorizontalContainer>
            <Paragraph color='primary'>
              {/* [BIS-1556] TODO: Once we support different kinds of liens, this will be dynamic. */}
              {/* Also, at the moment, we only receive liens back where the company looked up is a DEBTOR. */}
              UCC Financing Statement Filing {documentType && `· ${documentType}`} · Debtor
            </Paragraph>
          </div>
        </Card.Title>
        <Card.CollapsibleSection defaultExpanded title='Details'>
          <AttributeGrid horizontal='full-width'>
            <Attribute
              label='Filing Jurisdiction'
              value={jusrisdiction}
              placeholder='Unknown'
              redacted={isRedacted}
            />
            <Attribute
              label='Filing Date'
              value={filingDate}
              placeholder='Unknown'
              redacted={isRedacted}
            />
            <Attribute
              label='Expiration Date'
              value={expirationDate}
              placeholder='Unknown'
              redacted={isRedacted}
            />
            <Attribute
              label='Filing Reference'
              value={filingReference}
              placeholder='Unknown'
              redacted={isRedacted}
            />
            <Attribute
              label='Document Type'
              value={documentType}
              placeholder='Unknown'
              redacted={isRedacted}
            />
            <Attribute
              label='Original Filing Date'
              value={originalFilingDate}
              placeholder='Unknown'
              redacted={isRedacted}
            />
            <Attribute
              label='Original Filing Reference'
              value={originalFilingReference}
              placeholder='Unknown'
              redacted={isRedacted}
            />
          </AttributeGrid>
        </Card.CollapsibleSection>
        <Card.CollapsibleSection defaultExpanded title='Filing Office'>
          <AttributeGrid horizontal='full-width'>
            <Attribute
              label='Name'
              value={nameCase(filingOffice?.name)}
              placeholder='Unknown'
              redacted={isRedacted}
            />
            <Attribute
              label='Address'
              value={formatAddress({
                addressStreet1: filingOffice?.['street-address-1'] ?? '',
                addressStreet2: filingOffice?.['street-address-2'] ?? '',
                addressCity: filingOffice?.['address-city'] ?? '',
                addressSubdivision: filingOffice?.['address-region'] ?? '',
                addressPostalCode: filingOffice?.['postal-code'] ?? '',
                color: 'primary',
              })}
              placeholder='Unknown'
              redacted={isRedacted}
            />
          </AttributeGrid>
        </Card.CollapsibleSection>
        <Card.CollapsibleSection defaultExpanded title='Secured Parties'>
          <AttributeGrid horizontal='full-width'>
            {securedParties &&
              securedParties.map((securedParty, index) => {
                return (
                  <Fragment key={`secured-party__${index}`}>
                    <Attribute
                      label='Name'
                      value={nameCase(securedParty?.name)}
                      placeholder='Unknown'
                      redacted={isRedacted}
                    />
                    <Attribute
                      label='Address'
                      value={formatAddress({
                        addressStreet1: securedParty?.['street-address-1'] ?? '',
                        addressStreet2: securedParty?.['street-address-2'] ?? '',
                        addressCity: securedParty?.['address-city'] ?? '',
                        addressSubdivision: securedParty?.['address-region'] ?? '',
                        addressPostalCode: securedParty?.['postal-code'] ?? '',
                        color: 'primary',
                      })}
                      placeholder='Unknown'
                      redacted={isRedacted}
                    />
                  </Fragment>
                );
              })}
          </AttributeGrid>
        </Card.CollapsibleSection>
        <Card.CollapsibleSection defaultExpanded title='Debtors'>
          <AttributeGrid horizontal='full-width'>
            {debtors &&
              debtors.map((debtor, index) => {
                return (
                  <Fragment key={`debtor__${index}`}>
                    <Attribute
                      label='Name'
                      value={nameCase(debtor?.name)}
                      placeholder='Unknown'
                      redacted={isRedacted}
                    />
                    <Attribute
                      label='Address'
                      value={formatAddress({
                        addressStreet1: debtor?.['street-address-1'] ?? '',
                        addressStreet2: debtor?.['street-address-2'] ?? '',
                        addressCity: debtor?.['address-city'] ?? '',
                        addressSubdivision: debtor?.['address-region'] ?? '',
                        addressPostalCode: debtor?.['postal-code'] ?? '',
                        color: 'primary',
                      })}
                      placeholder='Unknown'
                      redacted={isRedacted}
                    />
                  </Fragment>
                );
              })}
          </AttributeGrid>
        </Card.CollapsibleSection>
        <Card.CollapsibleSection defaultExpanded title='Collaterals'>
          <UnorderedList>
            {collaterals &&
              collaterals.map((collateral, index) => {
                return (
                  <li key={`collateral__${index}`}>
                    {collateral?.['collateral-type']}{' '}
                    {collateral?.['collateral-supplemental-items']?.join(', ')}
                  </li>
                );
              })}
          </UnorderedList>
        </Card.CollapsibleSection>
      </Card.Body>
    </StyledCard>
  );
};

const Container = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;
  grid-template-columns: 300px auto;
`;

// Note: 168px is the sum of the report heading height + all the top and bottom paddings in the report view
// this is the value needed to make the page not scroll.
const SideNav = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${Spacing[3]} ${Spacing[4]};
  height: calc(100vh - 168px);
  grid-row: span 2;
  border-right: 1px solid ${({ theme }) => theme.color.border.primary};
`;

const SideNavFixedSizeContainer = styled.div`
  flex: 0 0 auto;
`;

// Note: 168px is the sum of the report heading height + all the top and bottom paddings in the report view
// this is the value needed to make the page not scroll.
const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 168px);
  overflow: auto;
  padding: ${Spacing[5]} ${Spacing[5]} 0px ${Spacing[5]};
  grid-row: 2;
  grid-column: 2;

  @media print {
    height: auto;
  }
`;

const StyledAttributeGrid = styled(AttributeGrid)`
  border-bottom: 1px solid ${({ theme }) => theme.color.border.primary};
  margin-top: ${Spacing[3]};
  margin-bottom: ${Spacing[3]};
  padding-bottom: ${Spacing[3]};
  padding-left: ${Spacing[3]};
`;

const StyledParagraph = styled(Paragraph)`
  padding-left: ${Spacing[3]};
`;

const SearchFilterBarContainer = styled.div`
  flex: 0 0 auto;
  margin-top: ${Spacing[3]};
  margin-bottom: ${Spacing[3]};
`;

const LiensContainer = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
`;

const ShownLienCount = ({
  searchedUCCFilings,
  uccFilings,
}: {
  searchedUCCFilings: UCCFiling[];
  uccFilings: UCCFiling[];
}) => {
  if (uccFilings.length === 0) return <Paragraph>No liens found</Paragraph>;
  return (
    <Paragraph>{`${
      searchedUCCFilings.length === uccFilings.length ? 'All' : searchedUCCFilings.length + ' of'
    } ${uccFilings.length > 1 ? uccFilings.length : ''} liens shown`}</Paragraph>
  );
};

export interface BusinessLiensLookupProps {
  report: ReportBusinessLiensLookup;
  isRedacted: boolean;
}

const BusinessLiensLookup = ({ report, isRedacted }: BusinessLiensLookupProps) => {
  const [search, setSearch] = useState('');

  const {
    metadata: { 'database-business-token': brvToken },
    'business-name': businessName,
  } = report.attributes;

  const uccFilings = report.attributes.result['ucc-filings'];
  const filteredUCCFilings = useMemo<UCCFiling[]>(() => uccFilings, [uccFilings]);
  const uccFilingsFuse = useMemo(() => {
    const fuseOptions = {
      shouldSort: false,
      threshold: 0.3,
      ignoreLocation: true,
      findAllMatches: true,
      keys: [
        'filing-date',
        'status',
        ['debtors', 'name'],
        ['filing-office', 'address-region'],
        ['filing-office', 'name'],
        ['secured-parties', 'name'],
      ],
    };

    return new Fuse(filteredUCCFilings, fuseOptions);
  }, [filteredUCCFilings]);

  const searchedUCCFilings = useMemo<UCCFiling[]>(
    () =>
      search === '' ? filteredUCCFilings : uccFilingsFuse.search(search).map(({ item }) => item),
    [filteredUCCFilings, search, uccFilingsFuse],
  );

  return (
    <Card>
      <Container>
        <SideNav>
          <SideNavFixedSizeContainer>
            <StyledParagraph size='lg' weight='bold' color='primary' as='h4'>
              Inputs
            </StyledParagraph>
            <StyledAttributeGrid>
              <Attribute
                label='Verified Business Record'
                value={<Link to={`/dashboard/verifications/${brvToken}`}>{brvToken}</Link>}
                redacted={isRedacted}
              />
            </StyledAttributeGrid>
          </SideNavFixedSizeContainer>
          <SideNavFixedSizeContainer>
            <StyledParagraph size='lg' weight='bold' color='primary' as='h4'>
              Business Details
            </StyledParagraph>
            <StyledAttributeGrid>
              <Attribute
                label='Business Name'
                value={nameCase(businessName)}
                redacted={isRedacted}
              />
              <Attribute label='Lien count' value={uccFilings.length} redacted={isRedacted} />
              <Attribute
                label='Open lien count'
                value={uccFilings.filter((filing) => filing.status === FilingStatus.Open).length}
                redacted={isRedacted}
              />
            </StyledAttributeGrid>
          </SideNavFixedSizeContainer>
        </SideNav>
        <MainContent>
          <>
            <Heading size='sm' data-chromatic='ignore'>
              Business Liens
            </Heading>
            <ShownLienCount searchedUCCFilings={searchedUCCFilings} uccFilings={uccFilings ?? []} />
            <SearchFilterBarContainer>
              <SearchInput
                id='business-liens__search'
                title='Search'
                handleInput={setSearch}
                value={search}
                placeholder='Search business liens'
                size='md'
              />
            </SearchFilterBarContainer>
            <LiensContainer>
              {(searchedUCCFilings ?? []).map((filing) => {
                return (
                  <UCCFilingDetails
                    key={`ucc-filing__${filing['filing-reference']}`}
                    filing={filing}
                    isRedacted={isRedacted}
                  />
                );
              })}
            </LiensContainer>
          </>
        </MainContent>
      </Container>
    </Card>
  );
};

export default BusinessLiensLookup;
